import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from './FormManager';
import './About.css';

// Import team images
import teamPhoto from '../assets/Team Photo.png';
import heikoPhoto from '../assets/Heiko.png';
import alexPhoto from '../assets/Alex.jpeg';
import franziPhoto from '../assets/Franzi.png';

// Import reference images


import { Helmet } from 'react-helmet';

const About = () => {
  const { t } = useTranslation();
  const { openGeneralForm } = useForm();
  
  // Refs for sections we want to animate
  const heroRef = useRef(null);
  const storyRef = useRef(null);
  const timelineRef = useRef(null);
  const teamRef = useRef(null);
  const valuesRef = useRef(null);
  const approachRef = useRef(null);
  const promiseRef = useRef(null);
  const uniqueRef = useRef(null);
  const ctaRef = useRef(null);
  
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);
    
    // Observe all section refs
    [heroRef, storyRef, timelineRef, teamRef, valuesRef, approachRef, promiseRef, uniqueRef, ctaRef].forEach(ref => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });
    
    return () => {
      [heroRef, storyRef, timelineRef, teamRef, valuesRef, approachRef, promiseRef, uniqueRef, ctaRef].forEach(ref => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Über Enzlo – Die führende Digitalagentur für Websites, SEO, Ads & Buchungssysteme in Frankfurt, Darmstadt, Stuttgart, Ulm & Umgebung</title>
        <meta name="description" content="Lernen Sie das Team und die Geschichte von Enzlo kennen – Ihre Full-Service Digitalagentur für Webdesign, SEO, Google Ads, Online-Buchungssysteme und mehr. Regional verwurzelt in Frankfurt, Darmstadt, Stuttgart, Ulm." />
        <link rel="canonical" href="https://enzlo.com/about" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": "Über Enzlo – Full-Service Digitalagentur",
            "description": "Lernen Sie das Team und die Geschichte von Enzlo kennen – Ihre Full-Service Digitalagentur für Webdesign, SEO, Google Ads, Online-Buchungssysteme und mehr. Regional verwurzelt in Frankfurt, Darmstadt, Stuttgart, Ulm.",
            "url": "https://enzlo.com/about",
            "mainEntity": {
              "@type": "Organization",
              "name": "Spoddig UG (haftungsbeschränkt)",
              "legalName": "Spoddig UG (haftungsbeschränkt), HRB 234397 B",
              "url": "https://enzlo.com/",
              "logo": "https://enzlo.com/assets/logo.png",
              "telephone": "+4915751619973",
              "sameAs": [
                "https://www.facebook.com/enzloinc/",
                "https://www.youtube.com/@Totalemailautoresponder"
              ],
              "contactPoint": [
                {
                  "@type": "ContactPoint",
                  "telephone": "+4915751619973",
                  "contactType": "customer service",
                  "areaServed": ["Frankfurt", "Darmstadt", "Aschaffenburg", "Stuttgart", "Ulm"]
                }
              ],
              "address": [
                {
                  "@type": "PostalAddress",
                  "streetAddress": "Eckweg 8",
                  "postalCode": "64354",
                  "addressLocality": "Reinheim",
                  "addressCountry": "DE"
                },
                {
                  "@type": "PostalAddress",
                  "streetAddress": "Zeppelinstraße 20",
                  "postalCode": "73084",
                  "addressLocality": "Salach",
                  "addressCountry": "DE"
                }
              ]
            }
          }
        `}</script>
        <script type="application/ld+json">{`
        [
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Webdesign Agentur",
            "description": "Professionelle Websites für Unternehmen in Frankfurt, Darmstadt, Aschaffenburg, Stuttgart, Ulm und Umgebung.",
            "areaServed": ["Frankfurt", "Darmstadt", "Aschaffenburg", "Stuttgart", "Ulm"]
          },
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "SEO Agentur",
            "description": "Suchmaschinenoptimierung (SEO) für lokale Unternehmen in Frankfurt, Darmstadt, Aschaffenburg, Stuttgart, Ulm.",
            "areaServed": ["Frankfurt", "Darmstadt", "Aschaffenburg", "Stuttgart", "Ulm"]
          },
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Google Ads Agentur",
            "description": "Google Ads Kampagnen für Unternehmen in Frankfurt, Darmstadt, Aschaffenburg, Stuttgart, Ulm.",
            "areaServed": ["Frankfurt", "Darmstadt", "Aschaffenburg", "Stuttgart", "Ulm"]
          },
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Online Buchungssystem",
            "description": "Online-Buchungslösungen für Dienstleister und Unternehmen in Frankfurt, Darmstadt, Aschaffenburg, Stuttgart, Ulm.",
            "areaServed": ["Frankfurt", "Darmstadt", "Aschaffenburg", "Stuttgart", "Ulm"]
          },
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Reputation Management",
            "description": "Automatisierte Generierung von Google Bewertungen für Unternehmen in Frankfurt, Darmstadt, Aschaffenburg, Stuttgart, Ulm.",
            "areaServed": ["Frankfurt", "Darmstadt", "Aschaffenburg", "Stuttgart", "Ulm"]
          },
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "E-Mail Marketing",
            "description": "E-Mail Marketing und Newsletter für Unternehmen in Frankfurt, Darmstadt, Aschaffenburg, Stuttgart, Ulm.",
            "areaServed": ["Frankfurt", "Darmstadt", "Aschaffenburg", "Stuttgart", "Ulm"]
          }
        ]
        `}</script>
      </Helmet>

      <div className="about-page">
      {/* Hero Section */}
      <section ref={heroRef} className="about-hero fade-in">
        <div className="about-hero-content">
          <h1 className="about-hero-title">{t('aboutHeroTitle')}</h1>
          <p className="about-hero-subtitle">{t('aboutHeroSubtitle')}</p>
        </div>
        <div className="about-hero-visual">
          <div className="about-hero-accent hero-shape-1"></div>
          <div className="about-hero-accent hero-shape-2"></div>
        </div>
      </section>
      
      {/* Story Section */}
      <section ref={storyRef} className="about-story fade-in">
        <div className="about-container">
          <h2 className="section-title">{t('aboutStoryTitle')}</h2>
          <div className="about-story-content">
            <div className="about-story-text">
              <p className="section-desc">{t('aboutStoryDesc')}</p>
            </div>
            <div className="about-story-image">
              <picture>
                <source srcSet={teamPhoto.replace(/\.(jpg|jpeg|png)$/, '.webp')} type="image/webp" />
                <img src={teamPhoto} alt={t('teamPhotoAlt')} className="team-photo" loading="lazy" />
              </picture>
            </div>
          </div>
        </div>
      </section>
      
      {/* Timeline Section */}
      <section ref={timelineRef} className="about-timeline fade-in">
        <div className="about-container">
          <h2 className="section-title">{t('aboutTimelineTitle')}</h2>
          <div className="timeline-wrapper">
            <div className="timeline-center-line"></div>
            
            <div className="timeline-item left">
              <div className="timeline-dot"></div>
              <div className="timeline-content">
                <div className="timeline-year">2011</div>
                <p>{t('aboutTimeline2011')}</p>
              </div>
            </div>
            
            <div className="timeline-item right">
              <div className="timeline-dot"></div>
              <div className="timeline-content">
                <div className="timeline-year">2014</div>
                <p>{t('aboutTimeline2014')}</p>
              </div>
            </div>
            
            <div className="timeline-item left">
              <div className="timeline-dot"></div>
              <div className="timeline-content">
                <div className="timeline-year">2017</div>
                <p>{t('aboutTimeline2017')}</p>
              </div>
            </div>
            
            <div className="timeline-item right">
              <div className="timeline-dot"></div>
              <div className="timeline-content">
                <div className="timeline-year">2020</div>
                <p>{t('aboutTimeline2020')}</p>
              </div>
            </div>
            
            <div className="timeline-item left">
              <div className="timeline-dot"></div>
              <div className="timeline-content">
                <div className="timeline-year">2024</div>
                <p>{t('aboutTimeline2024')}</p>
              </div>
            </div>
          </div>
          <div className="timeline-end-spacer"></div>
        </div>
      </section>
      
      {/* Team Section */}
      <section ref={teamRef} className="about-team fade-in">
        <div className="about-container">
          <h2 className="section-title">{t('aboutTeamTitle')}</h2>
          
          <div className="team-grid">
            <div className="team-member">
              <div className="team-member-photo heiko">
                <img src={heikoPhoto} alt={t('aboutHeikoAlt')} className="member-img" loading="lazy" />
                <div className="team-member-overlay">
                  <h3>{t('aboutHeikoName')}</h3>
                  <p className="team-member-role">{t('aboutHeikoRole')}</p>
                </div>
              </div>
              <p className="team-member-bio">{t('aboutHeikoDesc')}</p>
            </div>
            
            <div className="team-member">
              <div className="team-member-photo alex">
                <img src={alexPhoto} alt={t('aboutAlexAlt')} className="member-img" loading="lazy" />
                <div className="team-member-overlay">
                  <h3>{t('aboutAlexName')}</h3>
                  <p className="team-member-role">{t('aboutAlexRole')}</p>
                </div>
              </div>
              <p className="team-member-bio">{t('aboutAlexDesc')}</p>
            </div>
            
            <div className="team-member">
              <div className="team-member-photo franzi">
                <img src={franziPhoto} alt={t('aboutFranziAlt')} className="member-img" loading="lazy" />
                <div className="team-member-overlay">
                  <h3>{t('aboutFranziName')}</h3>
                  <p className="team-member-role">{t('aboutFranziRole')}</p>
                </div>
              </div>
              <p className="team-member-bio">{t('aboutFranziDesc')}</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Values Section */}
      <section ref={valuesRef} className="about-values fade-in">
        <div className="values-container">
          <div className="values-intro">
            <h2 className="section-title">{t('aboutValuesTitle')}</h2>
          </div>
          
          <div className="values-list">
            <div className="value-item">
              <div className="value-number">1</div>
              <h3>{t('aboutValueResults')}</h3>
              <p>{t('aboutValueResultsDesc')}</p>
            </div>
            
            <div className="value-item">
              <div className="value-number">2</div>
              <h3>{t('aboutValueSimplicity')}</h3>
              <p>{t('aboutValueSimplicityDesc')}</p>
            </div>
            
            <div className="value-item">
              <div className="value-number">3</div>
              <h3>{t('aboutValueImprovement')}</h3>
              <p>{t('aboutValueImprovementDesc')}</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Approach Section */}
      <section ref={approachRef} className="about-approach fade-in">
        <div className="about-container">
          <h2 className="section-title">{t('aboutApproachTitle')}</h2>
          <p className="section-desc">{t('aboutApproachDesc')}</p>
          
          <div className="approach-grid">
            <div className="approach-item">
              <h3>{t('aboutApproachPoint1')}</h3>
              <p>{t('aboutApproachPoint1Desc')}</p>
            </div>
            <div className="approach-item">
              <h3>{t('aboutApproachPoint2')}</h3>
              <p>{t('aboutApproachPoint2Desc')}</p>
            </div>
            <div className="approach-item">
              <h3>{t('aboutApproachPoint3')}</h3>
              <p>{t('aboutApproachPoint3Desc')}</p>
            </div>
            <div className="approach-item">
              <h3>{t('aboutApproachPoint4')}</h3>
              <p>{t('aboutApproachPoint4Desc')}</p>
            </div>
            <div className="approach-item">
              <h3>{t('aboutApproachPoint5')}</h3>
              <p>{t('aboutApproachPoint5Desc')}</p>
            </div>
            <div className="approach-item">
              <h3>{t('aboutApproachPoint6')}</h3>
              <p>{t('aboutApproachPoint6Desc')}</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Promise Section */}
      <section ref={promiseRef} className="about-promise fade-in">
        <div className="about-container">
          <h2 className="section-title">{t('aboutPromiseTitle')}</h2>
          <p className="section-desc">{t('aboutPromiseDesc')}</p>
          <div className="about-promise-layout">
            <div className="about-client-card">
              <h3>{t('aboutClientTitle')}</h3>
              <p>{t('aboutClientDesc')}</p>
            </div>
            <div className="about-bridge-bubble">
              <h3>{t('aboutBridgeTitle')}</h3>
              <p>{t('aboutBridgeDesc')}</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Combined Unique & CTA Section */}
      <section ref={uniqueRef} className="about-unique-cta fade-in">
        <div className="about-container">
          <div className="unique-cta-content">
            <div className="unique-section">
              <h2 className="section-title">{t('aboutUniqueTitle')}</h2>
              <div className="unique-points">
                <div className="unique-point">{t('aboutUniquePoint1')}</div>
                <div className="unique-point">{t('aboutUniquePoint2')}</div>
                <div className="unique-point">{t('aboutUniquePoint3')}</div>
                <div className="unique-point">{t('aboutUniquePoint4')}</div>
              </div>
            </div>
            
            <div className="cta-section">
              <h2>{t('aboutCtaTitle')}</h2>
              <button onClick={openGeneralForm}>{t('aboutCtaButton')}</button>
            </div>
          </div>
          
          <p className="about-footer-tagline">{t('aboutFooterTagline')}</p>
        </div>
      </section>
    </div>
  </>
  );
};

export default About;
